"use client";

import { useState } from "react";
import { UserProfile } from "@/utils/types";
import { SettingUserType } from "@/types/user.ts";
import { updateProfileService } from "@/services/profile.service.ts";

interface NotificationsSectionProps {
  profileData: UserProfile | null;
}

interface ToggleRowProps {
  id: string;
  name: string;
  description: string;
  defaultChecked: boolean;
  isLast?: boolean;
  onChange: (checked: boolean) => void;
}

function ToggleRow({ id, name, description, defaultChecked, isLast, onChange }: ToggleRowProps) {
  const [checked, setChecked] = useState(defaultChecked);

  function handleToggle() {
    const next = !checked;
    setChecked(next);
    onChange(next);
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 0",
        borderBottom: isLast ? "none" : "1px solid #1c1c1c",
        gap: "16px",
      }}
    >
      <div>
        <p style={{ fontSize: "11px", fontWeight: 500, color: "#b0ab9f", margin: 0 }}>{name}</p>
        <p style={{ fontSize: "10px", color: "#4a4a4a", marginTop: "2px" }}>{description}</p>
      </div>

      {/* Toggle switch */}
      <button
        role="switch"
        aria-checked={checked}
        id={id}
        onClick={handleToggle}
        style={{
          width: "36px",
          height: "20px",
          background: checked ? "#0d2b1a" : "#1a1a1a",
          border: checked ? "1px solid #1a4a2a" : "1px solid #272727",
          borderRadius: "20px",
          cursor: "pointer",
          position: "relative",
          flexShrink: 0,
          transition: "background 0.2s, border-color 0.2s",
          padding: 0,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: "50%",
            transform: `translateY(-50%) translateX(${checked ? "18px" : "2px"})`,
            width: "14px",
            height: "14px",
            borderRadius: "50%",
            background: checked ? "#3ecf7e" : "#3a3a3a",
            transition: "transform 0.2s, background 0.2s",
            display: "block",
          }}
        />
      </button>
    </div>
  );
}

export function NotificationsSection({ profileData }: NotificationsSectionProps) {
  const [updatedProfile, setUpdatedProfile] = useState(false);

  async function updateSetting(checked: boolean, settingOption: string) {
    const updatedSettings: SettingUserType = {
      ...profileData?.settings,
      [settingOption]: checked,
    };
    try {
      await updateProfileService({ settings: updatedSettings });
      setUpdatedProfile(true);
    } catch (err) {
      console.error("[NotificationsSection] Error updating notification setting:", err);
    }
  }

  return (
    <div
      style={{
        background: "#0e0e0e",
        border: "1px solid #1c1c1c",
        borderRadius: "14px",
        overflow: "hidden",
        marginBottom: "10px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "18px 20px",
          borderBottom: "1px solid #1c1c1c",
        }}
      >
        <div>
          <p style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.08em", color: "#c8c3ba", margin: 0 }}>
            NOTIFICATIONS
          </p>
          <p style={{ fontSize: "10px", color: "#4a4a4a", letterSpacing: "0.02em", marginTop: "2px" }}>
            Manage your notification preferences
          </p>
        </div>
        {updatedProfile && (
          <span style={{ fontSize: "10px", color: "#5aaa7a", letterSpacing: "0.04em" }}>
            SAVED
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: "0 20px" }}>
        <ToggleRow
          id="email-notifications"
          name="Email Notifications"
          description="Receive email updates about your account"
          defaultChecked={profileData?.settings?.emailNotification ?? true}
          onChange={(checked) => { void updateSetting(checked, "emailNotification"); }}
        />
        <ToggleRow
          id="marketing-emails"
          name="Marketing Emails"
          description="Receive emails about new features and updates"
          defaultChecked={profileData?.settings?.marketingEmails ?? false}
          onChange={(checked) => { void updateSetting(checked, "marketingEmails"); }}
        />
        <ToggleRow
          id="security-alerts"
          name="Security Alerts"
          description="Get notified about security updates"
          defaultChecked={profileData?.settings?.securityAlerts ?? false}
          isLast
          onChange={(checked) => { void updateSetting(checked, "securityAlerts"); }}
        />
      </div>
    </div>
  );
}
